import { type QuerySubSourceListItem, type QuerySubSourceListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { QuerySubSourceFormDrawer } from '../components/QuerySubSourceFormDrawer';
import { listQuerySubSources } from '../query-sub-sources.api';
import {
  useBulkDeleteQuerySubSources,
  useBulkSetQuerySubSourcesStatus,
  useDeleteQuerySubSource,
  useQuerySourceOptions,
  useQuerySubSourcesList,
} from '../use-query-sub-sources';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search only fires from this many characters. */
const MIN_SEARCH_LENGTH = 3;

export const QuerySubSourcesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteQuerySubSource = useDeleteQuerySubSource();
  const bulkDeleteMut = useBulkDeleteQuerySubSources();
  const bulkSetStatusMut = useBulkSetQuerySubSourcesStatus();
  const sourceOptionsQuery = useQuerySourceOptions();

  const sourceOptions: ReadonlyArray<{ value: string; label: string }> = useMemo(() => {
    const entries = (sourceOptionsQuery.data ?? []).map((s) => ({ value: s.id, label: s.name }));
    return [{ value: '', label: 'All sources' }, ...entries];
  }, [sourceOptionsQuery.data]);

  const filters: ReadonlyArray<DataGridFilter> = useMemo(
    () => [
      {
        id: 'source',
        label: 'Source',
        options: sourceOptions,
        widthClass: 'w-56',
      },
      {
        id: 'status',
        label: 'Status',
        options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
        widthClass: 'w-48',
      },
    ],
    [sourceOptions],
  );

  const columns: ListScreenColumns<QuerySubSourceListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<QuerySubSourceListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'querySource',
        header: 'Parent source',
        cell: (row) => <span className="text-zinc-700">{row.querySource.name}</span>,
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<QuerySubSourceListItem, QuerySubSourceListQuery>
      title="Query Sub Sources"
      description="Finer breakdown of a query source (e.g. referral → existing customer)."
      useList={useQuerySubSourcesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const source = state.filters.source;
        return {
          ...base,
          ...(source ? { querySourceId: source } : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add sub source' }}
      renderDrawer={({ open, mode, row, close }) => (
        <QuerySubSourceFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          querySubSource={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteQuerySubSource.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this query sub source?',
            description: `${row.name} will be soft-deleted and hidden from lists. Leads already classified with it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} query sub source${rows.length === 1 ? '' : 's'}?`,
            description: 'These query sub sources will be soft-deleted and hidden from lists.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} query sub source${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in lead capture and reports.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listQuerySubSources(q),
        filename: 'query-sub-sources',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Parent source', value: (r) => r.querySource.name },
          { header: 'Description', value: (r) => r.description ?? '' },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No query sub sources match these filters',
        emptyDescription: 'Adjust the source or status filter, or clear the search box.',
        errorTitle: 'Failed to load query sub sources',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
